// 宿 QR (2026-09-25, 民泊 PoC Step 1): landmark 編集 Sheet 内の「宿 QR (部屋ごと)」セクション。
//
// 部屋を追加すると推測されにくい token が発行され、enduser の `/q/<token>` を開く QR が表示される。
// ゲストはこの QR を読むと、ログインなし・GPS 判定なしで、この landmark (= 宿) のクーポンだけを受け取れる。
// 無効化した QR は通常フロー (トップ) に戻る。行は消さない (過去の取得履歴の流入元を残すため)。
//
// 表示は edit mode (= landmark.id 存在) のみ。auth は api 側 enforced (ADMIN 全件 / BUSINESS 自社 landmark)。
// Spec: design/kuroko-handoff-minpaku-step1-qr-guest-20260925.md ①

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { FacilityQrApi } from '@/apis';
import type { FacilityQrCode } from '@/apis/FacilityQrApi';
import { URL_CONFIG } from '@/configs/environment';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { observer } from 'mobx-react-lite';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useState } from 'react';

const api = new FacilityQrApi();

const buildQrUrl = (token: string) => `${URL_CONFIG.ENDUSER.url.replace(/\/$/, '')}/q/${token}`;

type Props = {
    landmark: any
};

const FacilityQrSection = observer(function FacilityQrSection({ landmark }: Props) {
    const { apiStore } = useStore();
    const [rows, setRows] = useState<FacilityQrCode[]>([]);
    const [roomLabel, setRoomLabel] = useState('');
    const [saving, setSaving] = useState(false);
    const [openQrId, setOpenQrId] = useState<number | null>(null);

    const reload = useCallback(async () => {
        if (!landmark?.id) return;
        try {
            const res: any = await apiStore.call(api, api.list, landmark.id, { hideLoading: true, disableAlertError: true });
            setRows(res?.ok ? (res.data?.items ?? []) : []);
        } catch {
            setRows([]);
        }
    }, [apiStore, landmark?.id]);

    useEffect(() => {
        reload();
        setOpenQrId(null);
    }, [reload]);

    const handleAdd = async () => {
        const label = roomLabel.trim();
        if (!label) {
            toastify('error', '部屋名を入力してください (例: 101)');
            return;
        }
        setSaving(true);
        try {
            const res: any = await apiStore.call(
                api,
                api.create,
                { landmarkId: landmark.id, roomLabel: label },
                { hideLoading: true, disableAlertError: true }
            );
            if (res?.ok) {
                toastify('success', `部屋「${label}」の QR を発行しました`);
                setRoomLabel('');
                setOpenQrId(Number(res.data?.id) || null);
                await reload();
            } else {
                toastify('error', res?.message || 'QR の発行に失敗しました');
            }
        } catch (e: any) {
            toastify('error', e?.data?.message || 'QR の発行に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (row: FacilityQrCode) => {
        try {
            const res: any = await apiStore.call(
                api,
                api.setActive,
                { landmarkId: landmark.id, id: row.id, isActive: !row.is_active },
                { hideLoading: true, disableAlertError: true }
            );
            if (res?.ok) {
                toastify('success', row.is_active ? 'QR を無効にしました' : 'QR を有効にしました');
                await reload();
            } else {
                toastify('error', res?.message || '更新に失敗しました');
            }
        } catch (e: any) {
            toastify('error', e?.data?.message || '更新に失敗しました');
        }
    };

    const handleCopy = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            toastify('success', 'URL をコピーしました');
        } catch {
            toastify('error', 'コピーに失敗しました');
        }
    };

    return (
        <div className='border border-background-6 !rounded-[5px] pb-4 mt-4'>
            <div className='px-6 py-3 text-[15px] font-semibold bg-background-6 rounded-t-sm'>
                宿 QR (部屋ごと)
            </div>
            <p className='px-6 mt-2 text-xs text-muted-foreground'>
                部屋に貼る QR を発行します。読んだゲストはログイン不要・位置情報なしで、このスポットのクーポンだけを受け取れます。
                占いの表示を出さない場合は「クーポン専用モード」を ON にしてください。
                1 日の配布上限と 1 人あたりの回数は「設定」画面で変更できます。
            </p>
            <div className='mt-4 px-6 space-y-3'>
                <div className='flex gap-2'>
                    <Input
                        value={roomLabel}
                        onChange={e => setRoomLabel(e.target.value)}
                        placeholder='部屋名 (例: 101)'
                        maxLength={64}
                        disabled={saving}
                        onKeyDown={(e) => {
                            // Sheet 全体の form submit (= landmark 更新) を誤発火させない
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAdd();
                            }
                        }}
                    />
                    <Button type='button' variant='active' onClick={handleAdd} disabled={saving}>
                        QR を発行
                    </Button>
                </div>

                {rows.length === 0 && (
                    <p className='text-sm text-muted-foreground'>まだ QR はありません。</p>
                )}

                {rows.map((row) => {
                    const url = buildQrUrl(row.token);
                    const isOpen = openQrId === row.id;
                    return (
                        <div key={row.id} className='border rounded p-3 space-y-2'>
                            <div className='flex items-center justify-between gap-2'>
                                <div className='text-sm'>
                                    <span className='font-semibold'>{row.room_label}</span>
                                    <span className={row.is_active ? 'ml-2 text-green-700' : 'ml-2 text-red-600'}>
                                        {row.is_active ? '有効' : '無効'}
                                    </span>
                                </div>
                                <div className='flex gap-2'>
                                    <Button type='button' variant='outline' size='sm' onClick={() => setOpenQrId(isOpen ? null : row.id)}>
                                        {isOpen ? 'QR を閉じる' : 'QR を表示'}
                                    </Button>
                                    <Button type='button' variant='outline' size='sm' onClick={() => handleToggle(row)}>
                                        {row.is_active ? '無効にする' : '有効にする'}
                                    </Button>
                                </div>
                            </div>
                            {isOpen && (
                                <div className='space-y-2'>
                                    <div className='flex justify-center bg-white p-3 border rounded'>
                                        <QRCodeSVG value={url} size={200} marginSize={2} />
                                    </div>
                                    <div className='flex gap-2'>
                                        <input
                                            type='text'
                                            value={url}
                                            readOnly
                                            className='flex-1 px-3 py-2 border rounded text-xs font-mono'
                                            onClick={e => (e.target as HTMLInputElement).select()}
                                        />
                                        <Button type='button' variant='outline' size='sm' onClick={() => handleCopy(url)}>
                                            コピー
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default FacilityQrSection;
